import { uploadAPI } from './api';

const IMGBB_API_KEY = '7def19a0bd71f46cad6d593fa476172f';
const IMGUR_CLIENT_ID = 'd70305e7c3ac5c6';
const IMGBB_UPLOAD_URL = 'https://api.imgbb.com/1/upload';
const IMGUR_UPLOAD_URL = 'https://api.imgur.com/3/upload';
const CLOUDINARY_CLOUD_NAME = 'dofhbg8h7';
const CLOUDINARY_UPLOAD_PRESET = 'autoboy_uploads';
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

/**
 * Convert file to base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} Base64 string without data URI prefix
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URI prefix (e.g., "data:image/jpeg;base64,")
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Upload image to ImgBB
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The hosted image URL
 */
export const uploadToImgBB = async (file) => {
  try {
    // Validate file
    if (!file || !file.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image.');
    }

    // Validate file size (ImgBB max: 32MB)
    const maxSize = 32 * 1024 * 1024; // 32MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size exceeds 32MB limit for ImgBB.');
    }

    // Convert file to base64
    const base64Image = await fileToBase64(file);

    // Create form data
    const formData = new FormData();
    formData.append('key', IMGBB_API_KEY);
    formData.append('image', base64Image);
    formData.append('name', file.name);

    // Upload to ImgBB
    const response = await fetch(IMGBB_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Failed to upload image to ImgBB');
    }

    // Return the hosted URL
    return data.data.url;
  } catch (error) {
    throw new Error(`ImgBB upload failed: ${error.message}`);
  }
};

/**
 * Upload image to Imgur
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The hosted image URL
 */
export const uploadToImgur = async (file) => {
  try {
    // Validate file
    if (!file || !file.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image.');
    }

    // Validate file size (Imgur max: 10MB for free tier)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size exceeds 10MB limit for Imgur.');
    }

    // Convert file to base64
    const base64Image = await fileToBase64(file);

    // Create form data
    const formData = new FormData();
    formData.append('image', base64Image);
    formData.append('type', 'base64');

    // Upload to Imgur
    const response = await fetch(IMGUR_UPLOAD_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Client-ID ${IMGUR_CLIENT_ID}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.data?.error || 'Failed to upload image to Imgur');
    }

    // Return the hosted URL
    return data.data.link;
  } catch (error) {
    throw new Error(`Imgur upload failed: ${error.message}`);
  }
};

/**
 * Upload image directly to Cloudinary (unsigned preset, no backend needed)
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The hosted image URL
 */
export const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

  const response = await fetch(CLOUDINARY_UPLOAD_URL, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(data.error?.message || 'Cloudinary direct upload failed');
  }

  return data.secure_url;
};

/**
 * Upload image with intelligent fallback strategy:
 * 1. Try Cloudinary direct (Primary)
 * 2. Try ImgBB (Fallback 1)
 * 3. Try Imgur (Fallback 2)
 * @param {File} file - The image file to upload
 * @param {Function} onProgress - Optional callback for upload progress
 * @returns {Promise<{url: string, platform: string}>} The hosted image URL and platform used
 */
export const uploadImage = async (file, onProgress = null) => {
  // 1. Try Cloudinary direct upload (bypasses backend)
  try {
    if (onProgress) onProgress({ platform: 'Cloudinary', status: 'uploading' });
    const url = await uploadToCloudinary(file);
    if (onProgress) onProgress({ platform: 'Cloudinary', status: 'success', url });
    return { url, platform: 'Cloudinary' };
  } catch (error) {
    console.warn('Cloudinary direct upload failed, attempting fallback to ImgBB...', error);
  }

  // 2. Fallback to ImgBB
  try {
    if (onProgress) onProgress({ platform: 'ImgBB', status: 'uploading' });
    const url = await uploadToImgBB(file);
    if (onProgress) onProgress({ platform: 'ImgBB', status: 'success', url });
    return { url, platform: 'ImgBB' };
  } catch (error) {
    console.warn('ImgBB upload failed, attempting fallback to Imgur...', error);
  }

  // 3. Fallback to Imgur
  try {
    if (onProgress) onProgress({ platform: 'Imgur', status: 'uploading' });
    const url = await uploadToImgur(file);
    if (onProgress) onProgress({ platform: 'Imgur', status: 'success', url });
    return { url, platform: 'Imgur' };
  } catch (error) {
    if (onProgress) onProgress({ status: 'error', error: 'All upload platforms failed.' });
    throw new Error('All upload platforms failed: ' + error.message);
  }
};

/**
 * Upload multiple images with progress tracking
 * @param {File[]} files - Array of image files to upload
 * @param {Function} onProgress - Callback for upload progress (fileIndex, total, result)
 * @returns {Promise<string[]>} Array of hosted image URLs
 */
export const uploadMultipleImages = async (files, onProgress = null) => {
  const results = [];
  const total = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      if (onProgress) {
        onProgress({
          index: i,
          total,
          fileName: file.name,
          status: 'uploading'
        });
      }

      const { url, platform } = await uploadImage(file);
      results.push(url);

      if (onProgress) {
        onProgress({
          index: i,
          total,
          fileName: file.name,
          status: 'success',
          url,
          platform
        });
      }
    } catch (error) {
      if (onProgress) {
        onProgress({
          index: i,
          total,
          fileName: file.name,
          status: 'error',
          error: error.message
        });
      }
      // Continue with other files even if one fails
    }
  }

  return results;
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result { valid: boolean, error: string }
 */
export const validateImageFile = (file, options = {}) => {
  const {
    maxSize = 32 * 1024 * 1024, // 32MB default (ImgBB limit)
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  } = options;

  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`
    };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`
    };
  }

  return { valid: true };
};

export default {
  uploadImage,
  uploadMultipleImages,
  validateImageFile,
};
